import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { acceptAll, rejectAll, updateConsent, hasStoredConsent, getStoredConsent } from "@/lib/consent";

export default function CookieBanner({ forceOpen, onDismiss }) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [detailed, setDetailed] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, ads: false, personalization: false });

  useEffect(() => {
    if (forceOpen) {
      const stored = getStoredConsent();
      setPrefs({
        analytics: stored?.analytics_storage === "granted",
        ads: stored?.ad_storage === "granted",
        personalization: stored?.ad_personalization === "granted" || stored?.personalization_storage === "granted",
      });
      setVisible(true);
      setDetailed(true);
    } else if (!hasStoredConsent()) {
      setVisible(true);
      setDetailed(false);
    }
  }, [forceOpen]);

  const close = () => { setVisible(false); onDismiss && onDismiss(); };

  const doAcceptAll = () => { acceptAll(); close(); };
  const doRejectAll = () => { rejectAll(); close(); };

  const doSave = () => {
    updateConsent({
      analytics_storage: prefs.analytics ? "granted" : "denied",
      ad_storage: prefs.ads ? "granted" : "denied",
      ad_user_data: prefs.ads ? "granted" : "denied",
      ad_personalization: prefs.personalization ? "granted" : "denied",
      personalization_storage: prefs.personalization ? "granted" : "denied",
    });
    close();
  };

  const cats = t("cookieBanner.categories");

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[100] p-4 md:p-6"
          data-testid="cookie-banner"
          role="dialog"
          aria-live="polite"
        >
          <div className="mx-auto max-w-[860px] rounded-2xl bg-[#1C352D] text-[#EAEFEA] shadow-[0_20px_60px_-20px_rgba(28,53,45,0.55)] border border-[#D4AF37]/25 overflow-hidden">
            <div className="p-6 md:p-7">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 shrink-0 rounded-full bg-[#D4AF37]/12 items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#D4AF37]">Google Consent Mode v2</div>
                      <h2 className="mt-2 font-serif text-xl sm:text-2xl leading-tight" data-testid="cookie-banner-title">{t("cookieBanner.title")}</h2>
                    </div>
                    <button className="text-[#EAEFEA]/50 hover:text-[#D4AF37]" onClick={doRejectAll} aria-label="Rifiuta e chiudi">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="mt-3 text-[13px] leading-relaxed text-[#EAEFEA]/80 max-w-[62ch]">{t("cookieBanner.desc")}</p>

                  {detailed && (
                    <div className="mt-6 space-y-3">
                      <PrefRow label={cats.necessary.title} desc={cats.necessary.desc} checked disabled testid="cookie-cat-necessary" alwaysOn={cats.necessary.always} />
                      <PrefRow label={cats.analytics.title} desc={cats.analytics.desc} checked={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} testid="cookie-cat-analytics" />
                      <PrefRow label={cats.ads.title} desc={cats.ads.desc} checked={prefs.ads} onChange={(v) => setPrefs((p) => ({ ...p, ads: v }))} testid="cookie-cat-ads" />
                      <PrefRow label={cats.personalization.title} desc={cats.personalization.desc} checked={prefs.personalization} onChange={(v) => setPrefs((p) => ({ ...p, personalization: v }))} testid="cookie-cat-personalization" />
                    </div>
                  )}

                  <div className="mt-6 flex flex-wrap items-center gap-2 md:gap-3">
                    {!detailed ? (
                      <>
                        <button onClick={doAcceptAll} data-testid="cookie-accept-all" className="rounded-full bg-[#D4AF37] px-5 py-2.5 text-[12.5px] font-medium text-[#1C352D] hover:bg-[#F7F2E6] transition-colors">
                          {t("cookieBanner.acceptAll")}
                        </button>
                        <button onClick={doRejectAll} data-testid="cookie-reject-all" className="rounded-full border border-[#EAEFEA]/25 px-5 py-2.5 text-[12.5px] font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                          {t("cookieBanner.rejectAll")}
                        </button>
                        <button onClick={() => setDetailed(true)} data-testid="cookie-manage" className="ml-auto text-[12px] underline underline-offset-4 hover:text-[#D4AF37]">
                          {t("cookieBanner.manage")}
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={doSave} data-testid="cookie-save" className="rounded-full bg-[#D4AF37] px-5 py-2.5 text-[12.5px] font-medium text-[#1C352D] hover:bg-[#F7F2E6] transition-colors">
                          {t("cookieBanner.save")}
                        </button>
                        <button onClick={doAcceptAll} data-testid="cookie-accept-all-detail" className="rounded-full border border-[#EAEFEA]/25 px-5 py-2.5 text-[12.5px] font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                          {t("cookieBanner.acceptAll")}
                        </button>
                        <button onClick={doRejectAll} data-testid="cookie-reject-all-detail" className="rounded-full border border-[#EAEFEA]/25 px-5 py-2.5 text-[12.5px] font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                          {t("cookieBanner.rejectAll")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PrefRow({ label, desc, checked, disabled, onChange, testid, alwaysOn }) {
  return (
    <div className="rounded-xl border border-[#EAEFEA]/12 bg-[#2D4A3E]/40 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-[#EAEFEA]">{label}</div>
          <p className="mt-1 text-[11.5px] leading-relaxed text-[#EAEFEA]/70">{desc}</p>
        </div>
        <div className="shrink-0">
          {disabled ? (
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#D4AF37]">{alwaysOn}</span>
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={checked}
              onClick={() => onChange(!checked)}
              data-testid={testid}
              className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${checked ? "bg-[#D4AF37]" : "bg-[#EAEFEA]/20"}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-[#F8F7F4] transition-transform ${checked ? "translate-x-5" : "translate-x-1"}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
